// GOLDEN PATH CODE
// This is a table backed by data entirely retrieved from react-query.
// Based on https://cloudscape.aws.dev/examples/react/server-side-table.html
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UseCollectionResult } from '@amzn/awsui-collection-hooks';
import {
  Button,
  CopyToClipboard,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from '@amzn/awsui-components-react';

import { AttendanceStatus } from '@amzn/red-velvet-api';

import { useTranslation } from 'react-i18next';
import { useTableStrings } from '../../../hooks/localization/tableStrings';
import { usePaginationStrings } from '../../../hooks/localization/paginationStrings';
import { useAttendeeSessionStorage } from '../../../hooks/attendance/attendeeSessionStorage';
import { useTableColumns } from '../../../hooks/tableColumns';
import { getValueFromSearchParam } from '../../../utils/url';
import { useParticipants } from '../../../hooks/attendance/useParticipants';
import { TablePropertyFilter } from '../wizard/tablePropertyFilter';
import { usePropertyFilterStrings } from '../../../hooks/localization/propertyFilterStrings';
import {
  useParticipantSessionStorage,
  ParamsParticipantData,
} from '../../../hooks/attendance/participantSessionStorage';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';
import {
  Amazonian,
  columnMap,
  shiftAttendanceColumnMap,
  getAliases,
} from '../../../utils/attendance/peopleSearch';
import { publishKatalMetric } from '../../katalAnalytics';
import { PeoplePreferences } from '../wizard/peoplePreferences';

import { AttendanceSelect } from '../coordinator/attendanceSelect';
import { useUser } from '../../../hooks/user';
import { featureIsEnabled, useFeatures } from '../../../hooks/features';
import { TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT } from '../../../utils/constants';

export type AttendeeTableProps = {
  isImmutable?: boolean;
  onAddAttendees?: () => void;
} & Partial<TableProps>;

export type ParticipationStatus = AttendanceStatus | 'PENDING';

export function CsvActions(props: {
  selectedItems: readonly Amazonian[];
  collection: UseCollectionResult<Amazonian>;
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const { aliases, removeAttendees } = useAttendeeSessionStorage();

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <CopyToClipboard
        copyButtonText={t('attendeeTable.copyAttendees')}
        copyErrorText={t('attendeeTable.copyAttendeesFailed')}
        copySuccessText={t('attendeeTable.copyAttendeesSuccess')}
        data-aci-analytics-name="attendance_copyAliasList"
        textToCopy={
          props.selectedItems.length > 0
            ? getAliases(props.selectedItems).join(',')
            : aliases.join(',')
        }
      />
      <Button
        variant="primary"
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          if (props.collection.collectionProps.onSelectionChange) {
            const emptyItems: Amazonian[] = [];
            props.collection.collectionProps.onSelectionChange({
              detail: { selectedItems: emptyItems },
            });
          }
          props.collection.paginationProps.onChange({ detail: { currentPageIndex: 1 } });
          const aliasesToRemove = getAliases(props.selectedItems);
          const urlAlises = aliasesToRemove.filter((alias) => aliases.includes(alias));
          removeAttendees(urlAlises);
          publishKatalMetric('attendance_removeAttendees', aliases.length.toString());
        }}
      >
        {t('attendeeTable.removeAttendees')}
      </Button>
    </SpaceBetween>
  );
}

export function TrackedActions(props: {
  selectedItems: readonly Amazonian[];
  collection: UseCollectionResult<Amazonian>;
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipantsFromParticipantData } = useBulkParticipantSessionStorage();

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          const aliases = getAliases(props.selectedItems);
          const mixin: ParamsParticipantData = {};
          aliases.forEach(
            (alias) =>
              (mixin[alias] = {
                attendance: 'DID_NOT_ATTEND',
              })
          );
          updateParticipants(mixin);
          updateBulkParticipantsFromParticipantData(mixin);
          publishKatalMetric('attendance_removeAttendeesPage1', aliases.length.toString());
        }}
      >
        {t('attendeeTable.coordinatorTracking.setDidNotAttend')}
      </Button>
      <Button
        variant="primary"
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          const aliases = getAliases(props.selectedItems);
          const mixin: ParamsParticipantData = {};
          aliases.forEach(
            (alias) =>
              (mixin[alias] = {
                attendance: 'ATTENDED',
              })
          );
          updateParticipants(mixin);
          updateBulkParticipantsFromParticipantData(mixin);
          publishKatalMetric('attendance_addAttendeesPage1', aliases.length.toString());
        }}
      >
        {t('attendeeTable.coordinatorTracking.setAttended')}
      </Button>
    </SpaceBetween>
  );
}

export function AttendeeTable(props: AttendeeTableProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);
  const { userId } = useUser();
  const features = useFeatures();
  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipantsFromParticipantData } = useBulkParticipantSessionStorage();

  const [searchParams] = useSearchParams();
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  const tableStrings = useTableStrings('attendeeTable', (item: Amazonian) => item.name, namespaces);
  const paginationStrings = usePaginationStrings('attendeeTable', namespaces);
  const colMap = eventId && shiftId ? shiftAttendanceColumnMap : columnMap;
  const columns = useTableColumns(colMap, 'attendeeTable', namespaces, {
    attendance: {
      errorIconAriaLabel: 'State Validation Error',
      editingCell: (item, { currentValue, setValue }) => (
        <AttendanceSelect
          participant={item}
          currentValue={currentValue || item.attendance}
          onAttendanceChange={(status: ParticipationStatus) => {
            setValue(status);
          }}
        />
      ),
      disabledReason: (item) =>
        item.alias === item.trackedBy && item.alias !== userId
          ? t('coordinatorAttendeeTable.selfTracked', { ns: 'attendance' })
          : undefined,
    },
  });

  // GOLDEN PATH NOTE
  // Table controls such as selectedItems, pagination, and filters should be controlled by state if it is unlikely
  // that the user cares to replicate them across sessions
  const [pageSize, setPageSize] = useState(100);

  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', namespaces);
  const collection = useParticipants(
    namespaces,
    'coordinatorAttendeeTable',
    eventId,
    shiftId,
    pageSize,
    true
  );
  const { items, participants, isLoading, collectionProps, paginationProps, propertyFilterProps } =
    collection;

  const { selectedItems = [] } = collectionProps;
  const isBulkUploadSingleShiftEnabled = featureIsEnabled(
    TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT,
    features,
    searchParams
  );

  return (
    <Table
      {...props}
      header={
        <Header
          actions={
            props.isImmutable ? null : (
              <SpaceBetween direction="horizontal" size="xs">
                {isBulkUploadSingleShiftEnabled && props.onAddAttendees && (
                  <Button onClick={props.onAddAttendees}>
                    {t('bulkUpload.addAttendeesAction')}
                  </Button>
                )}
                <TrackedActions collection={collection} selectedItems={selectedItems} />
              </SpaceBetween>
            )
          }
          counter={
            selectedItems.length
              ? `(${selectedItems.length}/${participants.length})`
              : `(${participants.length})`
          }
        >
          {t('attendeeTable.title')}
        </Header>
      }
      filter={
        <TablePropertyFilter
          {...propertyFilterProps}
          propertyFilterStrings={propertyFilterStrings}
          filteringPlaceholder={t('coordinatorAttendeeTable.filteringPlaceholder')}
        />
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
      preferences={
        <PeoplePreferences
          onConfirm={({ detail }) => {
            paginationProps.onChange({ detail: { currentPageIndex: 1 } });
            setPageSize(detail.pageSize || 100);
          }}
          preferences={{
            pageSize,
          }}
        />
      }
      selectionType={props.isImmutable ? undefined : 'multi'}
      submitEdit={(item, column, newVal) => {
        const mixin = {
          [item.alias || '']: {
            attendance: newVal as string,
          },
        };
        updateParticipants(mixin);
        updateBulkParticipantsFromParticipantData(mixin);
      }}
      // Do not allow modifications if the user tracked their own attendance
      isItemDisabled={(item) => item.alias === item.trackedBy && item.alias !== userId}
      {...tableStrings}
      {...columns}
      {...collectionProps}
      loading={isLoading}
      items={items}
      trackBy="alias"
      data-testid={'attendee-table'}
    />
  );
}
